import React from 'react';
import { TrendingUp, Banknote, MapPin, Wrench, Shield, CheckCircle, Leaf, Sun } from 'lucide-react';
import { DraggableElement } from '../DraggableElement';
import { EnPowerWordmarkInline } from '../EnPowerLogo';

const stats = [
  { value: '500+', label: 'MW Projektpipeline', sub: 'Europaweit' },
  { value: '4.5', label: 'GWp Portfolio (ILOS)', sub: '7 Länder' },
  { value: '100+', label: 'Realisierte Projekte', sub: 'EN:POWER' },
  { value: '30+', label: 'Jahre Vertragslaufzeit', sub: 'Langfristige Sicherheit' },
];

const benefits = [
  { icon: TrendingUp, ac: '#E07000', title: 'Gemeinsame Expertise', desc: 'EN:POWER und ILOS vereinen lokales Know-how mit internationaler IPP-Erfahrung. Von der Projektentwicklung bis zum langjährigen Betrieb profitieren Sie von einem eingespielten Team mit nachweislicher Erfolgshistorie.' },
  { icon: Banknote, ac: '#122A5C', title: 'Gesicherte Pachteinnahmen', desc: 'Langfristige Pachtzahlungen pro Hektar – vertraglich fixiert und indexiert. Ihre Einnahmen steigen in drei Stufen über die gesamte Laufzeit und bieten finanzielle Planungssicherheit über Jahrzehnte.' },
  { icon: MapPin, ac: '#E07000', title: 'Wertsteigerung Ihrer Fläche', desc: 'Die Nutzung als Solarpark wertet Ihre Freifläche langfristig auf. Parallele landwirtschaftliche Nutzung (Agri-PV) oder ökologische Aufwertung durch Biodiversitätsmaßnahmen sind möglich.' },
  { icon: Wrench, ac: '#122A5C', title: 'Komplette Projektabwicklung', desc: 'Wir übernehmen sämtliche Schritte: Flächenanalyse, Genehmigungsverfahren, Netzanschluss, Bau und 30+ Jahre Betrieb. Sie müssen sich um nichts kümmern – wir managen das gesamte Projekt.' },
  { icon: Shield, ac: '#E07000', title: 'Kein Risiko für Sie', desc: 'Alle Investitionskosten, technischen Risiken und Betriebsverantwortung liegen bei EN:POWER und ILOS. Sie stellen nur die Fläche bereit und erhalten dafür verlässliche Pachtzahlungen.' },
  { icon: CheckCircle, ac: '#122A5C', title: 'Keine Kosten', desc: 'Von der ersten Machbarkeitsstudie über Planung und Installation bis zum laufenden Betrieb: Es entstehen Ihnen keinerlei Kosten. Auch Rückbaugarantien sind vertraglich gesichert.' },
];

export const PageFooter = ({ page }) => (
  <div className="page-footer">
    <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', fontWeight: 700, color: '#E07000', letterSpacing: '0.04em' }}>
      <EnPowerWordmarkInline enColor="#122A5C" powerColor="#E07000" fontFamily="Manrope, sans-serif" fontSize="8px" fontWeight={700} letterSpacing="0.04em" /> <span style={{ color: '#94A3B8', fontWeight: 400 }}>&times;</span> <span style={{ color: '#122A5C' }}>ILOS</span>
    </span>
    <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', color: '#CBD5E1', letterSpacing: '0.02em' }}>Freiflächen-Photovoltaik &bull; Flächenverpachtung &bull; Gemeinschaftsprojekt</span>
    <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: '8.5px', fontWeight: 600, color: '#CBD5E1' }}>{page}</span>
  </div>
);

export const ContentPage2Static = ({ editMode = false, showMarkers = true, selectedElements = [], elementPositions = {}, onSelectElement, onPositionChange }) => {
  const dragProps = {
    editMode,
    showMarkers,
    selectedElements,
    elementPositions,
    onSelectElement,
    onPositionChange,
    dragAxis: 'y',
  };

  return (
    <div className="a4-page" data-testid="page-2" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Heading */}
      <DraggableElement id="FF-S2-Heading" {...dragProps}>
      <div style={{ padding: '32px 48px 18px', background: 'white', flexShrink: 0 }}>
        <span className="section-overline">
          <EnPowerWordmarkInline enColor="#122A5C" powerColor="#E07000" fontFamily="Manrope, sans-serif" fontSize="9px" fontWeight={700} letterSpacing="0.16em" /> &times; ILOS &bull; Partnerschaft
        </span>
        <h2 className="proposal-h2" style={{ fontSize: '19px', marginBottom: '0' }}>FREIFLÄCHEN-PHOTOVOLTAIK MIT ZUKUNFT</h2>
        <div className="orange-rule" style={{ marginBottom: '0' }} />
      </div>
      </DraggableElement>

      {/* Intro 2-col + Quote card */}
      <DraggableElement id="FF-S2-Intro" {...dragProps}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: '18px', padding: '0 48px 20px', background: 'white', flexShrink: 0, minHeight: '200px', boxSizing: 'border-box' }}>
        <DraggableElement id="FF-S2-Intro-Text" {...dragProps} initialStyle={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <p className="proposal-body" style={{ fontSize: '10.5px', marginBottom: '9px' }}>
              EN:POWER und ILOS Energy bündeln ihre Kräfte für die Entwicklung großflächiger
              Photovoltaik-Projekte auf Freiflächen. Als Gemeinschaftsprojekt verbinden wir lokale
              Marktkenntnis mit internationaler IPP-Erfahrung – für nachhaltige Energieprojekte,
              die Landbesitzern, Gemeinden und der Umwelt zugutekommen.
            </p>
            <p className="proposal-body" style={{ fontSize: '10.5px' }}>
              ILOS, unterstützt durch AXA IM Alts, betreibt ein europäisches Portfolio
              von 4,5 GWp und ist in 7 Ländern aktiv. EN:POWER bringt über 10 Jahre
              Erfahrung in der deutschen PV-Projektentwicklung ein.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
            {[
              { t: '50 MWp Freiflächenanlage', l: 'Hessen', d: 'In Entwicklung' },
              { t: '35 MWp Solarpark', l: 'Brandenburg', d: 'Genehmigung erteilt' },
              { t: '120 MWp Portfolio', l: 'Niedersachsen', d: 'Gemeinschaftsprojekt EN:POWER × ILOS' },
            ].map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#E07000', flexShrink: 0 }} />
                <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '9px', color: '#475569', margin: 0 }}>
                  <strong style={{ color: '#122A5C' }}>{r.t}</strong>{' \u2022 '}{r.l}{' \u2022 '}
                  <span style={{ color: '#94A3B8' }}>{r.d}</span>
                </p>
              </div>
            ))}
          </div>
        </DraggableElement>

        {/* Quote card */}
        <DraggableElement id="FF-S2-Intro-Quote" {...dragProps}>
        <div style={{ background: '#122A5C', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', boxSizing: 'border-box' }}>
          <Sun size={20} color="#E07000" strokeWidth={1.5} />
          <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '12px', fontWeight: 700, color: 'white', lineHeight: 1.35, margin: '10px 0' }}>
            &bdquo;Ihre Fläche erzeugt saubere Energie – Sie kassieren die Pacht.&ldquo;
          </p>
          <div>
            <div style={{ width: '28px', height: '2px', background: '#E07000', borderRadius: '1px', marginBottom: '6px' }} />
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.4 }}>
              <EnPowerWordmarkInline enColor="#FFFFFF" fontSize="8px" /> &times; ILOS – Langfristige Partnerschaft für Ihre Freifläche.
            </p>
          </div>
        </div>
        </DraggableElement>
      </div>
      </DraggableElement>

      {/* Stats strip */}
      <DraggableElement id="FF-S2-Stats" {...dragProps}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', background: '#F8FAFC', borderTop: '1px solid #E2E8F0', borderBottom: '1px solid #E2E8F0', flexShrink: 0, minHeight: '128px', padding: '20px 0', boxSizing: 'border-box' }}>
        {stats.map((s, i) => (
          <DraggableElement key={i} id={`FF-S2-Stat-${i + 1}`} {...dragProps} initialStyle={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', padding: '0 8px', height: '100%', borderRight: i < 3 ? '1px solid #E2E8F0' : 'none' }}>
            <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '28px', fontWeight: 800, color: '#122A5C', letterSpacing: '-0.03em', margin: '0 0 2px' }}>{s.value}</p>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '1px' }}>{s.label}</p>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', color: '#94A3B8', margin: 0 }}>{s.sub}</p>
          </DraggableElement>
        ))}
      </div>
      </DraggableElement>

      <div style={{ height: '1px', background: '#E2E8F0', flexShrink: 0 }} />

      {/* Benefits heading */}
      <DraggableElement id="FF-S2-Benefits-Heading" {...dragProps}>
      <div style={{ padding: '28px 48px 8px', background: 'white', flexShrink: 0 }}>
        <span className="section-overline">Was Sie gewinnen</span>
        <h2 className="proposal-h2" style={{ fontSize: '19px', marginBottom: '0' }}>IHRE VORTEILE AUF EINEN BLICK</h2>
        <div className="orange-rule" style={{ marginBottom: '0' }} />
      </div>
      </DraggableElement>

      {/* Benefits cards */}
      <DraggableElement id="FF-S2-Benefits" {...dragProps}>
      <div style={{ padding: '40px 48px 40px', background: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridTemplateRows: 'auto auto', gap: '20px', width: '100%', maxWidth: '100%' }}>
          {benefits.map((b, i) => {
            const Icon = b.icon;
            return (
              <DraggableElement key={i} id={`FF-S2-Benefit-${i + 1}`} {...dragProps}>
              <div style={{ border: '1px solid #E2E8F0', borderRadius: '12px', padding: '24px 18px', background: '#FAFBFC', display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: '245px', maxHeight: '245px' }}>
                <div style={{ width: '56px', height: '56px', background: b.ac, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px', flexShrink: 0 }}>
                  <Icon size={26} color="white" strokeWidth={2} />
                </div>
                <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '12px', fontWeight: 700, color: '#122A5C', marginBottom: '10px', lineHeight: 1.3, flexShrink: 0 }}>{b.title}</h3>
                <p className="proposal-body" style={{ fontSize: '9.5px', lineHeight: 1.65, color: '#64748B', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 8, WebkitBoxOrient: 'vertical' }}>{b.desc}</p>
              </div>
              </DraggableElement>
            );
          })}
        </div>
      </div>
      </DraggableElement>

      <PageFooter page={2} />
    </div>
  );
};
